import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';

import CourseCard from '../components/ui/CourseCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import {
  fetchAllCoursesExceptOwned,
  forkCourseWithUnits,
} from '../services/supabaseClient/courseService';
import { addCourse } from '../redux/coursesSlice';
import './Dashboard.css';

const CoursesPage = () => {
  const dispatch = useDispatch();

  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addedCourseIds, setAddedCourseIds] = useState(new Set());

  useEffect(() => {
    const loadCourses = async () => {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await fetchAllCoursesExceptOwned();

      if (fetchError) {
        setError(fetchError.message || 'Failed to load courses');
      } else {
        setCourses(data);
        setFilteredCourses(data);
      }

      setLoading(false);
    };

    loadCourses();
  }, []);

  // Filter courses based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCourses(courses);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = courses.filter(
        (course) =>
          course.title?.toLowerCase().includes(query) ||
          course.description?.toLowerCase().includes(query)
      );
      setFilteredCourses(filtered);
    }
  }, [searchQuery, courses]);

  const handleForkCourse = async (course) => {
    // Prevent adding if already added
    if (addedCourseIds.has(course.id)) {
      return;
    }

    try {
      const { data: forkedCourse, error: forkError } = await forkCourseWithUnits(course);

      if (forkError) {
        alert(`Failed to add course: ${forkError.message || forkError}`);
        return;
      }

      // Add to Redux store
      dispatch(addCourse(forkedCourse));

      // Mark this course as added
      setAddedCourseIds((prev) => new Set(prev).add(course.id));

      // Show success message
      alert(`Course "${course.title}" has been added to your dashboard!`);
    } catch (err) {
      alert(`Error adding course: ${err.message}`);
    }
  };

  const handleCourseSelect = () => {
    // For non-owned courses, we can show details but not start them directly
    // The user needs to fork first
    alert('Please click "Add Course" to add this course to your dashboard');
  };

  return (
    <Container className="dashboard py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Find Courses</h2>
      </div>

      {/* Search Bar */}
      <div className="mb-4" style={{ position: 'relative', maxWidth: '500px' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Search courses..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            paddingRight: '40px',
            borderRadius: '12px',
            border: '1px solid var(--c-border)',
            background: 'var(--c-bg)',
            color: 'var(--c-text)',
          }}
        />
        <FaSearch
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--c-text-subtle)',
            pointerEvents: 'none',
          }}
        />
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : courses.length === 0 ? (
        <div className="text-center py-5">
          <h4>No courses available</h4>
          <p>Be the first to create a course!</p>
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="text-center py-5">
          <h4>No courses found</h4>
          <p>Try a different search term</p>
        </div>
      ) : (
        <Row>
          {filteredCourses.map((course) => (
            <Col key={course.id} xs={12} md={6} lg={4} className="mb-4">
              <CourseCard
                course={course}
                onSelect={handleCourseSelect}
                onFork={handleForkCourse}
                isAdded={addedCourseIds.has(course.id)}
              />
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

export default CoursesPage;
